// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type Ref,
} from 'react';

import { ApiRequestError, api } from '../../api/client';
import { isTerminal, type ModelEditEntry, type ModelEditOp, type ModelRevision, type ModelSnap } from '../../api/types';
import { useSettings } from '../../components/bygning/settings/SettingsContext';
import { useCanEdit } from '../CaseRoleContext';
import { useJobs } from '../JobsContext';
import { useAsync } from '../useAsync';
import type { GeometriModel } from '../../bygning/geometri';
import { deleteOps, editLabel, revertOps } from '../../bygning/geometriEdit';
import { commandDef, type GeometriCommand, type GeometriTool } from '../../bygning/geometriKeys';
import { cycle } from '../../bygning/geometriSelect';
import { failureKind, failureMessage, settle, withPending, type PendingEdit } from '../../bygning/geometriSync';
import type { XY } from '../../bygning/planWalls';

/** The status line's snap toggles (SketchUp / Spacio): the inference engine (S3) reads them. */
export interface SnapToggles {
  /** Snap at all (Alt suspends it for one drag). */
  snap: boolean;
  /** Axis locks in the storey's Manhattan frame. */
  ortho: boolean;
  /** Detected plane traces (`GET /model/snap`). */
  trace: boolean;
  /** The 5 mm grid. */
  grid: boolean;
}

export const DEFAULT_SNAP: SnapToggles = { snap: true, ortho: true, trace: true, grid: false };

/** A value many small things read often (the cursor's plan position): kept out of React state. */
interface Store<T> {
  get(): T;
  set(value: T): void;
  subscribe(listener: () => void): () => void;
}

function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (next) => {
      if (next === value) return;
      value = next;
      for (const l of listeners) l();
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export interface GeometriEditor {
  /** Rediger › Geometri is the page shown: the editor reads, draws and takes keys. */
  active: boolean;
  canEdit: boolean;
  /** The building model with every pending batch on top. */
  model: GeometriModel;
  loading: boolean;
  /** The selected guids, the primary (`?el=`) first. */
  selection: readonly string[];
  select(guids: readonly string[]): void;
  tool: GeometriTool;
  setTool(tool: GeometriTool): void;
  snap: SnapToggles;
  toggleSnap(key: keyof SnapToggles): void;
  /** `GET /model/revision` (null before it is read, or on a server without the route). */
  revision: ModelRevision | null;
  /** The edit log, newest first (null before it is read). */
  history: readonly ModelEditEntry[] | null;
  pending: readonly PendingEdit[];
  /** A solids run is queued or running. */
  rederiving: boolean;
  /** Send a batch (optimistic); resolves true when the server took it. */
  commit(ops: ModelEditOp[], label: string): Promise<boolean>;
  run(command: GeometriCommand): void;
  /** What lies under the last click, for Tab. */
  setCandidates(guids: readonly string[]): void;
  /** The pointer's plan position (the status line's readout). */
  cursor: Store<XY | null>;
  /**
   * A grip drag or a typed dimension in progress (S3): what the status
   * line says instead of the tool's hint, and the drag's readout. While it
   * is set, the editor's keys are the gesture's (digits, Tab, Enter, Esc).
   */
  gesture: Store<GestureStatus | null>;
  /** `GET /model/snap` for the storey the plan shows: wall axes, Manhattan angles, plane traces (null before it is read). */
  snapTargets: ModelSnap | null;
}

/** The status line's view of a gesture. */
export interface GestureStatus {
  hint: { lead: string; keys: { key: string; text: string }[] };
  readout: string | null;
}

const EMPTY_MODEL: GeometriModel = { storeys: null, walls: null, openings: null, spaces: null, slabs: null };

const GeometriEditorContext = createContext<GeometriEditor | null>(null);

/** A gesture's status, for the status line. */
export function useEditorGesture(editor: GeometriEditor): GestureStatus | null {
  return useSyncExternalStore(editor.gesture.subscribe, editor.gesture.get, editor.gesture.get);
}

/** The editor, or null outside the Bygning shell. */
export function useGeometriEditor(): GeometriEditor | null {
  return useContext(GeometriEditorContext);
}

/** The cursor's plan position, for the status line's readout. */
export function useEditorCursor(editor: GeometriEditor): XY | null {
  return useSyncExternalStore(editor.cursor.subscribe, editor.cursor.get, editor.cursor.get);
}

/** A list the server may not have (an older ruxd without the route): null. */
async function orNull<T>(run: () => Promise<T>, signal: AbortSignal): Promise<T | null> {
  try {
    return await run();
  } catch (cause) {
    if (signal.aborted) throw cause;
    return null;
  }
}

export interface GeometriEditorHandle {
  run(command: GeometriCommand): void;
}

export interface GeometriEditorProviderProps {
  /** Rediger › Geometri is shown. */
  active: boolean;
  /** `?el=`: the primary selection, the shell's. */
  element: string | null;
  setElement(guid: string | null): void;
  /** Switch the Ground between the locked plan and 3D. */
  setPlan(plan: boolean): void;
  /** Frame the whole model. */
  frameAll(): void;
  notify(message: string): void;
  /** For the shell's ⌘K palette and keyboard. */
  handleRef?: Ref<GeometriEditorHandle>;
  children: ReactNode;
}

/**
 * Rediger › Geometri's editor state (frontend-u0o.9), shared by the mode's
 * chrome (tool dock, selection bar, Egenskaber, status line), the Ground's
 * plan layer and the shell's ⌘K palette: the building model (read once for
 * all of them, re-read on `model.changed`, a finished job and every write),
 * the multi-selection (its primary is the URL's `?el=`), the tool, the snap
 * toggles and the write loop — optimistic batches against the model
 * revision, a stale revision re-read and replayed once, undo / redo, the
 * edit log and the solids' re-derive.
 */
export function GeometriEditorProvider({
  active,
  element,
  setElement,
  setPlan,
  frameAll,
  notify,
  handleRef,
  children,
}: GeometriEditorProviderProps) {
  // A viewer reads the model and selects; every write is refused before the request.
  const canEdit = useCanEdit();
  const { jobs, modelChange } = useJobs();
  const finished = jobs.filter((j) => isTerminal(j.status)).length;
  // A solids run of ours (or anyone's) still going: "Dæk & tag genberegnes…".
  const rederiving = jobs.some((j) => j.stage === 'solids' && !isTerminal(j.status));

  // ------------------------------------------------------------ reads --
  // `tick` numbers the reads: a write bumps it, and a batch stays on top
  // of the model until the read it bumped to has landed.
  const [tick, setTickState] = useState(0);
  const tickRef = useRef(0);
  /** Ask for a fresh read; returns the number it will carry. */
  const bump = useCallback(() => {
    tickRef.current += 1;
    setTickState(tickRef.current);
    return tickRef.current;
  }, []);
  const eventRevision = modelChange?.revision ?? null;
  const read = useAsync(
    async (signal) => {
      if (!active) return { tick, model: EMPTY_MODEL };
      const [storeys, walls, openings, spaces, slabs] = await Promise.all([
        orNull(() => api.storeys(signal), signal),
        orNull(() => api.walls(signal), signal),
        orNull(() => api.openings(signal), signal),
        orNull(() => api.spaces(signal), signal),
        orNull(() => api.slabs(signal), signal),
      ]);
      return { tick, model: { storeys, walls, openings, spaces, slabs } as GeometriModel };
    },
    [active, finished, tick, eventRevision],
  );
  const revisionRead = useAsync(
    (signal) => (active ? orNull(() => api.modelRevision(signal), signal) : Promise.resolve(null)),
    [active, finished, tick, eventRevision],
  );
  // What the inference engine snaps to on the storey the plan shows (S3).
  const { state: settingsState } = useSettings();
  const snapStorey = settingsState.buildingElements.storey.storeyId;
  const snapRead = useAsync(
    (signal) => (active && snapStorey !== null ? orNull(() => api.modelSnap(snapStorey, signal), signal) : Promise.resolve(null)),
    [active, snapStorey, tick, eventRevision],
  );
  const historyRead = useAsync(
    (signal) => (active ? orNull(() => api.modelHistory(undefined, signal), signal) : Promise.resolve(null)),
    [active, tick, eventRevision],
  );
  const revision = revisionRead.data ?? null;
  const revisionRef = useRef<number | null>(null);
  revisionRef.current = revision?.revision ?? revisionRef.current;

  const [pending, setPending] = useState<PendingEdit[]>([]);
  const loadedTick = read.data?.tick ?? -1;
  useEffect(() => setPending((p) => settle(p, loadedTick)), [loadedTick]);
  const model = useMemo(() => withPending(read.data?.model ?? EMPTY_MODEL, pending), [read.data, pending]);
  const modelRef = useRef(model);
  modelRef.current = model;

  // -------------------------------------------------------- selection --
  const [extra, setExtra] = useState<string[]>([]);
  const lastSet = useRef<string | null>(element);
  useEffect(() => {
    // Someone else moved `?el=` (the tree, the 3D pick, Esc): the extras go.
    if (element !== lastSet.current) {
      lastSet.current = element;
      setExtra([]);
    }
  }, [element]);
  const selection = useMemo(() => (element === null ? [] : [element, ...extra.filter((g) => g !== element)]), [element, extra]);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const select = useCallback(
    (guids: readonly string[]) => {
      const primary = guids[0] ?? null;
      lastSet.current = primary;
      setExtra(guids.slice(1));
      setElement(primary);
    },
    [setElement],
  );
  // Leaving Geometri drops the picks.
  useEffect(() => {
    if (!active) setExtra([]);
  }, [active]);

  const [tool, setToolState] = useState<GeometriTool>('select');
  const [snap, setSnap] = useState<SnapToggles>(DEFAULT_SNAP);
  const toggleSnap = useCallback((key: keyof SnapToggles) => setSnap((s) => ({ ...s, [key]: !s[key] })), []);
  const setTool = useCallback(
    (t: GeometriTool) => {
      const def = commandDef(`tool:${t}`);
      if (!def.ready) {
        notify(`${def.label} kommer snart`);
        return;
      }
      setToolState(t);
    },
    [notify],
  );
  const candidates = useRef<readonly string[]>([]);
  const setCandidates = useCallback((guids: readonly string[]) => {
    candidates.current = guids;
  }, []);
  const cursor = useMemo(() => createStore<XY | null>(null), []);
  const gesture = useMemo(() => createStore<GestureStatus | null>(null), []);

  // ------------------------------------------------------------ writes --
  const nextId = useRef(1);
  const commit = useCallback(
    async (ops: ModelEditOp[], label: string): Promise<boolean> => {
      if (!canEdit || ops.length === 0) return false;
      const id = nextId.current++;
      setPending((p) => [...p, { id, label, ops, state: 'sending' }]);
      const send = (base: number | null) =>
        api.applyModelEdits({ ...(base !== null ? { base_revision: base } : {}), label, ops });
      try {
        let result;
        try {
          result = await send(revisionRef.current);
        } catch (cause) {
          // A stale revision: read it again and replay the batch once.
          if (!(cause instanceof ApiRequestError) || failureKind(cause.status, cause.code) !== 'conflict') throw cause;
          const now = await api.modelRevision();
          revisionRef.current = now.revision;
          result = await send(now.revision);
        }
        revisionRef.current = result.revision;
        for (const w of result.warnings) notify(w);
        const next = bump();
        setPending((p) => p.map((e) => (e.id === id ? { ...e, state: 'saved', tick: next } : e)));
        return true;
      } catch (cause) {
        setPending((p) => p.filter((e) => e.id !== id));
        const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
        notify(failureMessage(kind, label, cause instanceof Error ? cause.message : undefined));
        bump();
        return false;
      }
    },
    [canEdit, notify, bump],
  );

  const undoRedo = useCallback(
    async (which: 'undo' | 'redo') => {
      if (!canEdit) return;
      const label = which === 'undo' ? 'Fortryd' : 'Gentag';
      const base = revisionRef.current;
      try {
        const request = base !== null ? { base_revision: base } : {};
        const result = which === 'undo' ? await api.undoModelEdit(request) : await api.redoModelEdit(request);
        revisionRef.current = result.revision;
      } catch (cause) {
        const kind = cause instanceof ApiRequestError ? failureKind(cause.status, cause.code) : 'error';
        notify(failureMessage(kind, label, cause instanceof Error ? cause.message : undefined));
      }
      bump();
    },
    [canEdit, notify, bump],
  );

  const rederive = useCallback(async () => {
    if (!canEdit) return;
    try {
      await api.rederiveSolids();
      notify('Dæk og tag genberegnes…');
    } catch (cause) {
      notify(failureMessage('error', 'Genberegning', cause instanceof Error ? cause.message : undefined));
    }
  }, [canEdit, notify]);

  const run = useCallback(
    (command: GeometriCommand) => {
      const sel = selectionRef.current;
      const m = modelRef.current;
      if (command.startsWith('tool:')) {
        setTool(command.slice(5) as GeometriTool);
        return;
      }
      switch (command) {
        case 'delete': {
          const ops = deleteOps(m, sel);
          if (ops.length === 0) return;
          select([]);
          void commit(ops, editLabel('Slet', m, sel));
          return;
        }
        case 'revert': {
          const ops = revertOps(m, sel);
          if (ops.length > 0) void commit(ops, editLabel('Tilbage til detekteret', m, sel));
          return;
        }
        case 'undo':
        case 'redo':
          void undoRedo(command);
          return;
        case 'escape':
          if (sel.length > 0) select([]);
          return;
        case 'cycle':
        case 'cycle-back': {
          const next = cycle(candidates.current, sel[0] ?? null, command === 'cycle' ? 1 : -1);
          if (next !== null) select([next]);
          return;
        }
        case 'view:plan':
          setPlan(true);
          return;
        case 'view:3d':
          setPlan(false);
          return;
        case 'zoom-fit':
          frameAll();
          return;
        case 'rederive':
          void rederive();
          return;
        default: {
          const def = commandDef(command);
          if (!def.ready) notify(`${def.label} (${def.keys}) kommer snart`);
        }
      }
    },
    [setTool, select, commit, undoRedo, setPlan, frameAll, rederive, notify],
  );
  useImperativeHandle(handleRef, () => ({ run }), [run]);

  const history = useMemo(() => (historyRead.data ? [...historyRead.data.entries].reverse() : null), [historyRead.data]);

  const value = useMemo<GeometriEditor>(
    () => ({
      active,
      canEdit,
      model,
      loading: active && read.data?.model === undefined,
      selection,
      select,
      tool,
      setTool,
      snap,
      toggleSnap,
      revision,
      history,
      pending,
      rederiving,
      commit,
      run,
      setCandidates,
      cursor,
      gesture,
      snapTargets: snapRead.data ?? null,
    }),
    [active, canEdit, model, read.data, selection, select, tool, setTool, snap, toggleSnap, revision, history, pending, rederiving, commit, run, setCandidates, cursor, gesture, snapRead.data],
  );
  return <GeometriEditorContext.Provider value={value}>{children}</GeometriEditorContext.Provider>;
}
